import { forwardRef, type HTMLAttributes } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { getInitials } from "@/lib/utils";
import { warningAccentBackgroundStyle } from "@/components/ui/feedback/warningVisual";

export type AvatarProps = HTMLAttributes<HTMLDivElement> & {
  src?: string;
  alt?: string;
  name?: string;
  size?: "sm" | "md" | "lg";
  status?: "online" | "busy" | "offline";
  circleClassName?: string;
  imageClassName?: string;
};

type AvatarSize = "sm" | "md" | "lg";
type AvatarStatus = "online" | "busy" | "offline";

/* Exact compiled output of the pre-migration Tailwind utilities. */

const avatarSizeStyles: Record<AvatarSize, CSSObject> = {
  sm: { width: "2rem", height: "2rem", fontSize: ".75rem", lineHeight: "1rem" },
  md: { width: "2.5rem", height: "2.5rem", fontSize: ".875rem", lineHeight: "1.25rem" },
  lg: { width: "3rem", height: "3rem", fontSize: "1rem", lineHeight: "1.5rem" }
};

const avatarStatusStyles: Record<AvatarStatus, CSSObject> = {
  online: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-success) / var(--tw-bg-opacity, 1))"
  },
  busy: {},
  offline: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-text-disabled) / var(--tw-bg-opacity, 1))"
  }
};

const AvatarRoot = styled("div", {
  label: "AgenticAvatar-root"
})({
  position: "relative",
  display: "inline-flex",
  flexShrink: 0
});

const AvatarCircle = styled("div", {
  label: "AgenticAvatar-circle",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { size: AvatarSize } }>(({ ownerState }) => [
  {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    borderRadius: "var(--bfrs-radius-pill)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
    fontWeight: 700,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
  },
  avatarSizeStyles[ownerState.size]
]);

const AvatarImage = styled("img", {
  label: "AgenticAvatar-image"
})({
  width: "100%",
  height: "100%",
  objectFit: "cover"
});

const AvatarStatusDot = styled("span", {
  label: "AgenticAvatar-status",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { status: AvatarStatus } }>(({ ownerState }) => [
  {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: ".75rem",
    height: ".75rem",
    borderRadius: "var(--bfrs-radius-pill)",
    borderWidth: "2px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-border-opacity, 1))"
  },
  avatarStatusStyles[ownerState.status]
]);

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(({ className, circleClassName, imageClassName, src, alt, name, size = "md", status, ...props }, ref) => (
  <AvatarRoot ref={ref} className={className} {...props}>
    <AvatarCircle ownerState={{ size }} className={circleClassName}>
      {src ? <AvatarImage src={src} alt={alt ?? name ?? ""} className={imageClassName} /> : <span>{getInitials(name)}</span>}
    </AvatarCircle>
    {status && <AvatarStatusDot ownerState={{ status }} style={status === "busy" ? warningAccentBackgroundStyle : undefined} aria-label={status} />}
  </AvatarRoot>
));

Avatar.displayName = "Avatar";
